import { modals } from '@mantine/modals';
import classNames from 'classnames/bind';
import { NavButton } from 'components/NavButton';
import { PageTitle } from 'components/PageTitle';
import { SuperButton } from 'components/SuperButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { TaskForm } from 'modules/Tasks';
import { useContext, useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useHistory } from 'react-router';
import { isNonNullish } from 'remeda';
import { InvoiceActionsDropdown } from 'shared/components';
import AYSModal from 'shared/components/AYSModal';
import { IconName } from 'shared/components/Icon';
import { LinkInvoiceForm, type LinkInvoiceFormFields } from 'shared/forms/LinkInvoiceForm';
import { Button, ButtonColor } from 'shared/io';
import { useCanUseManualActions } from 'shared/utils/connector';
import { isPaid } from 'shared/utils/invoice-status';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { CreditNote } from 'types/store/credit-note-state';
import InvoiceDetailContext from './InvoiceDetail.context';
import { InvoiceOtherActionsMenu } from './InvoiceOtherActionsMenu/InvoiceOtherActionsMenu';
import InvoiceSendingOptions from './InvoiceSendingOptions';
import MarkedInvoiceActions from './MarkedInvoiceActions';
import styleIdentifiers from './ToHandleDetail.module.scss';

const styles = classNames.bind(styleIdentifiers);

export default function InvoiceDetailTitle() {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();
  const history = useHistory();
  const hasManualActionsActive = useCanUseManualActions();
  const {
    data,
    isCreditNote: credit,
    unmatchPayment,
    reloadInfo,
  } = useContext(InvoiceDetailContext);

  const settleCreditNote = (settle: any) => () => {
    creditNoteActions.settle({
      id: data!.id,
      data: {
        settle,
      },
      callback: () => {
        creditNoteActions.info({
          id: data!.id,
          noReset: true,
          noLoading: true,
        });
      },
    });
  };

  const linkToInvoices: SubmitHandler<LinkInvoiceFormFields> = values => {
    const invoicesToLink = values.invoices_to_link
      .filter(e => e.amount > 0)
      .filter(
        e =>
          !(data as CreditNote).matched_payments_attributes.some(
            el => Number(el.invoice_attributes.id) === Number(e.invoice_id),
          ),
      )
      .map(e => ({
        id: e.invoice_id,
        amount: e.amount,
      }));

    creditNoteActions.linkToInvoices({
      data: {
        id: data.id,
        invoices_to_link: invoicesToLink,
      },
      callback: () => {
        (reloadInfo as any)();
        dialogHide(DialogShowId.CUSTOM);
      },
    });
  };

  const linkCreditNote = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.LARGE,
      keepMountOnExit: true,
      title: t(i18nKeys.INVOICE.LINK_TO_INVOICE),
      children: (
        <LinkInvoiceForm
          onSubmit={linkToInvoices}
          creditNote={data as CreditNote}
          reload={reloadInfo}
        />
      ),
    });
  };

  const duplicate = () => {
    dialogHide(DialogShowId.INVOICE);
    if (!data) return;

    if (credit) {
      creditNoteActions.copy({
        id: data.id,
        callback: (responseData: any) => history.push(`/credit-notes/${responseData.data.id}/edit`),
      });
    } else {
      invoiceActions.copy({
        id: data.id,
        callback: (responseData: any) => {
          history.push(`/invoices/${responseData.data.id}/edit`);
        },
      });
    }
  };

  const deleteInvoice = () => {
    showDialog({
      id: DialogShowId.CONFIRM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.DELETE),
      keepMountOnExit: true,
      children: (
        <AYSModal
          text={t(
            credit
              ? i18nKeys.INVOICE.AYS_TO_DELETE_CREDIT_NOTE
              : i18nKeys.AYS.INVOICE_ACTION_DELETE,
          )}
          onConfirm={() => {
            deleteItem();
          }}
        />
      ),
    });
  };

  // Possibly obsolete, thridparties have been removed from the invoice actions field
  const noRecoverActionAvailable = useMemo(
    () =>
      !data.actions.some(action => {
        switch (action) {
          case 'additional_reminder':
          case 'bailiff':
          case 'formal_notice':
          case 'lawyer':
            return true;
          default:
            return false;
        }
      }),
    [data?.actions],
  );

  const ignoreLateFees = () => {
    showDialog({
      id: DialogShowId.CONFIRM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.CONFIRMATION),
      children: (
        <AYSModal
          text={t(i18nKeys.AYS.IGNORE_LATE_FEES)}
          onConfirm={() => {
            invoiceActions.actionsIgnoreLateFees({
              id: data.id,
              callback: () => {
                (reloadInfo as any)();
              },
            });
          }}
        />
      ),
    });
  };

  const openSoForm = () => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.INVOICE.SENDING_OPTIONS),
      children: (
        <InvoiceSendingOptions
          invoiceId={data.id}
          sendingOptions={data.sending_options_attributes}
          debtorId={data.debtor_id}
        />
      ),
    });
  };

  const deleteItem = () => {
    if (!data) return;

    (credit ? creditNoteActions : invoiceActions).delete({
      id: data.id,
      callback: () => {
        dialogHide(DialogShowId.INVOICE);
        history.push(credit ? '/credit-notes' : '/invoices');
      },
    });
  };

  const updateExcludedFromRecoveryPlan = (value: boolean) => {
    if (!data) return;

    creditNoteActions.update({
      id: data.id,
      data: {
        id: data.id,
        excluded_from_recovery_plan: value,
      },
      callback: () => {
        (reloadInfo as any)();
      },
    });
  };

  const addTask = () => {
    modals.open({
      title: t(i18nKeys.FORM.TASK.NEW),
      size: 'xl',
      children: <TaskForm invoiceId={data.id.toString()} debtorId={data.debtor_id.toString()} />,
    });
  };

  const canLinkOrSettle = isPaid(data) || Number(data.remaining_balance) === 0;

  return (
    <PageTitle>
      <PageTitle.Back />
      {data.reference || data.id}
      <PageTitle.Actions>
        <SuperButton
          onClick={deleteItem}
          disabledMessage={
            isNonNullish(data.third_party_case_id) ? t('SUPERADMIN.WARNING_MESSAGES.INVOICE') : null
          }
        >
          {t(i18nKeys.DELETE)}
        </SuperButton>
        {credit ? (
          <>
            {isNonNullish(data.ongoing_payment_plan_id) ? (
              <NavButton
                to={`/payment-plans/${data.ongoing_payment_plan_id}`}
                label={t(i18nKeys.INVOICE.IN_PAYMENT_PLAN, { id: data.ongoing_payment_plan_id })}
              />
            ) : data.excluded_from_recovery_plan ? (
              <Button
                className={styles('button')}
                noMargin
                noShadow
                label={t(i18nKeys.INVOICE.INCLUDE_IN_RECOVERY_PLAN)}
                onClick={() => updateExcludedFromRecoveryPlan(false)}
                color={ButtonColor.GREY}
              />
            ) : (
              <Button
                className={styles('button')}
                noMargin
                noShadow
                label={t(i18nKeys.INVOICE.EXCLUDE_FROM_RECOVERY_PLAN)}
                onClick={() => updateExcludedFromRecoveryPlan(true)}
                color={ButtonColor.RED}
              />
            )}
            {hasManualActionsActive && data.actions.includes('delete') && (
              <Button
                className={styles('button')}
                noMargin
                noShadow
                label={t(i18nKeys.DELETE)}
                onClick={deleteInvoice}
                color={ButtonColor.GREY}
              />
            )}
            {hasManualActionsActive && (
              <Button
                className={styles('button')}
                noMargin
                noShadow
                label={t(i18nKeys.INVOICE.LINK_TO_INVOICE)}
                disabled={canLinkOrSettle}
                onClick={linkCreditNote}
              />
            )}
            <Button
              className={styles('button')}
              noMargin
              noShadow
              label={t(i18nKeys.INVOICE.BALANCE_CREDIT_NOTE)}
              onClick={settleCreditNote(true)}
              disabled={canLinkOrSettle}
              color={ButtonColor.GREEN}
            />
          </>
        ) : (
          <>
            <InvoiceOtherActionsMenu
              trigger={
                <div className={styles('marked-invoice-actions')}>
                  <Button
                    noShadow
                    noMargin
                    label={t(i18nKeys.INVOICE.OTHER_ACTIONS)}
                    className={styles('button')}
                    color={ButtonColor.GREY}
                    iconRight={IconName.MINIMAL_DOWN}
                  />
                </div>
              }
              canUseTasks={company.package.can_use_tasks}
              hasManualActions={hasManualActionsActive}
              canUnmatchPayment={data.actions.includes('unmatch_payment')}
              canDelete={data.actions.includes('delete')}
              showIgnoreLateFees={Boolean(data.late_fees_calculated_at)}
              canIgnoreLateFees={data.actions.includes('ignore_late_fees')}
              onNewTask={addTask}
              onEditSendingOptions={openSoForm}
              onDuplicate={duplicate}
              onUnmatchPayment={unmatchPayment}
              onDelete={deleteInvoice}
              onIgnoreLateFees={ignoreLateFees}
            />
            {/* -------------------------------------- */}
            <MarkedInvoiceActions
              invoice={data}
              element={
                <Button
                  noShadow
                  noMargin
                  label={t(i18nKeys.INVOICE.CHANGE_STATUS)}
                  className={styles('button')}
                  iconRight={IconName.MINIMAL_DOWN}
                />
              }
            />
            <InvoiceActionsDropdown
              reload={reloadInfo}
              invoice={data}
              selectorClass={styles(noRecoverActionAvailable && 'disabled')}
              item={
                <Button
                  noShadow
                  noMargin
                  label={t(i18nKeys.INVOICE.RECOVER_ACTIONS)}
                  disabled={noRecoverActionAvailable}
                  className={styles('button')}
                  color={ButtonColor.GREEN}
                  iconRight={IconName.MINIMAL_DOWN}
                />
              }
            />
          </>
        )}
      </PageTitle.Actions>
    </PageTitle>
  );
}
